import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Edge, Node } from 'reactflow';
import { McpServerService } from '../../../../service/McpServerService';
import type { McpToolItem } from '../../../../service/McpServerService';
import { NotificationType } from '../../../../store/storeNotifications';
import type {
  McpCallToolRequestPayload,
  McpNodeFormState,
} from '../../../../types/mcpNode';
import { executeErrorHandle } from '../../../../util/executeErrorHandle';
import { replacePlaceholders } from '../../../../util/myFunctionsCoreDisplay';

type SetSaving = (saving: {
  lastSaved?: string;
  status: string;
  finished?: boolean;
  flowId?: string;
}) => void;

type SetSchema = (
  schema: Record<string, { type: string; required: boolean }>,
) => void;

type ObjectCallerItem = {
  id: string;
  text: { text: string; id: string }[];
  name: string;
};

type McpHandleTextSegment = { text: string };

function joinMcpHandleSegments(value: unknown): string {
  if (!Array.isArray(value) || value.length === 0) {
    return '';
  }
  return value
    .map(segment => {
      if (
        typeof segment === 'object' &&
        segment !== null &&
        'text' in segment &&
        typeof (segment as McpHandleTextSegment).text === 'string'
      ) {
        return (segment as McpHandleTextSegment).text;
      }
      return '';
    })
    .join('');
}

function readMcpFlowTriggerId(
  nodeData: Record<string, unknown> | undefined,
): string | undefined {
  if (nodeData === undefined) {
    return undefined;
  }
  const raw = nodeData.flowTrigger;
  if (typeof raw !== 'object' || raw === null) {
    return undefined;
  }
  const id = (raw as Record<string, unknown>).id;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

export interface UseMcpSubmitParams {
  id: string;
  data: Record<string, unknown> | undefined;
  edges: Edge[];
  flowId: string | undefined;
  schema: Record<string, { type: string; required: boolean }> | undefined;
  organizationId: string;
  rawMcpServerUrl: string;
  formData: McpNodeFormState;
  availableTools: McpToolItem[];
  socket: { id?: string; connected?: boolean } | null | undefined;
  setActiveNodeSockets: Dispatch<SetStateAction<string[]>>;
  pendingClientRequestIdRef: MutableRefObject<string | null>;
  mcpResponseTimeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  clearMcpResponseTimeout: () => void;
  setNodeState: Dispatch<SetStateAction<Node[]>>;
  setSaving: SetSaving;
  setSchema: SetSchema;
  setFormData: Dispatch<SetStateAction<McpNodeFormState>>;
  setNotification: (n: { type: string; message: string }) => void;
  onSave: (object: Record<string, unknown>, changeType?: string) => void;
  userObjects: ObjectCallerItem[];
  activeNodeSockets: string[];
}

const RESPONSE_TIMEOUT_MS = 120000;

export function useMcpSubmit(params: UseMcpSubmitParams) {
  const {
    id,
    data,
    edges,
    flowId,
    schema,
    organizationId,
    rawMcpServerUrl,
    formData,
    availableTools,
    socket,
    setActiveNodeSockets,
    pendingClientRequestIdRef,
    mcpResponseTimeoutRef,
    clearMcpResponseTimeout,
    setNodeState,
    setSaving,
    setSchema,
    setFormData,
    setNotification,
    onSave,
    userObjects,
    activeNodeSockets,
  } = params;

  const [isLoading, setIsLoading] = useState(false);
  const [hasToExecute, setHasToExecute] = useState(true);
  const [executeCounter, setExecuteCounter] = useState(0);

  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCallToolRef = useRef(() => Promise.resolve());

  const handleCallTool = useCallback(async () => {
    const serverUrl = replacePlaceholders(rawMcpServerUrl, schema);

    const toolNameJoined = joinMcpHandleSegments(data?.toolNameData);
    const toolName = toolNameJoined || formData.toolName;
    const promptJoined = joinMcpHandleSegments(data?.promptData);
    const prompt = promptJoined
      ? replacePlaceholders(promptJoined, schema)
      : replacePlaceholders(formData.prompt, schema);

    const authTokenJoined = joinMcpHandleSegments(data?.authTokenData);
    const authToken = authTokenJoined
      ? replacePlaceholders(authTokenJoined, schema)
      : replacePlaceholders(formData.authToken, schema);

    const savedUserIntegrationId = formData.integrationId?.trim();
    if (!savedUserIntegrationId && !serverUrl?.trim()) {
      setNotification({
        message:
          'Enter an MCP server URL, or select a saved MCP (HTTP) connection from Integrations.',
        type: NotificationType.Error,
      });
      return;
    }
    if (savedUserIntegrationId && !organizationId) {
      setNotification({
        message: 'Select a workspace to use a saved MCP connection.',
        type: NotificationType.Error,
      });
      return;
    }

    const toolNameResolved = (toolName ?? '').trim();
    if (availableTools.length > 0 && toolNameResolved) {
      const knownNames = new Set(availableTools.map(t => t.name));
      if (!knownNames.has(toolNameResolved)) {
        setNotification({
          message: `"${toolNameResolved}" is not available on this server. Select a tool from the list.`,
          type: NotificationType.Error,
        });
        return;
      }
    }

    if (!(prompt ?? '').trim()) {
      setNotification({
        message: 'Enter a prompt.',
        type: NotificationType.Error,
      });
      return;
    }

    if (!socket?.id || !socket?.connected) {
      setNotification({
        message: 'Connection not ready. Please wait and try again.',
        type: NotificationType.Warning,
      });
      return;
    }

    setIsLoading(true);

    try {
      setActiveNodeSockets((prev: string[]) =>
        prev.includes(id) ? prev : [...prev, id],
      );

      const clientRequestId = crypto.randomUUID();
      pendingClientRequestIdRef.current = clientRequestId;

      const selectedTool = toolNameResolved
        ? availableTools.find(t => t.name === toolNameResolved)
        : null;
      const toolContext = selectedTool
        ? {
            name: selectedTool.name,
            description: selectedTool.description,
            inputSchema: selectedTool.inputSchema,
          }
        : undefined;

      const useSavedHttpIntegration = Boolean(savedUserIntegrationId);
      const resolvedAuthForRequest = useSavedHttpIntegration
        ? undefined
        : authToken?.trim() || undefined;

      const requestBody: McpCallToolRequestPayload = {
        ...(useSavedHttpIntegration && organizationId
          ? {
              integrationId: savedUserIntegrationId,
              organizationId,
            }
          : { serverUrl }),
        toolName: toolName || undefined,
        prompt: prompt || undefined,
        authToken: resolvedAuthForRequest,
        socketId: socket.id,
        organizationId: organizationId || undefined,
        toolContext,
        clientRequestId,
        nodeId: id,
        flowId: flowId || undefined,
      };

      await new Promise<void>((resolve, reject) => {
        setTimeout(() => {
          McpServerService.request(requestBody)
            .then(() => resolve())
            .catch(reject);
        }, 0);
      });

      if (mcpResponseTimeoutRef.current) {
        clearTimeout(mcpResponseTimeoutRef.current);
      }
      mcpResponseTimeoutRef.current = setTimeout(() => {
        mcpResponseTimeoutRef.current = null;
        pendingClientRequestIdRef.current = null;
        setActiveNodeSockets((prev: string[]) =>
          prev.filter((item: string) => item !== id),
        );
        setIsLoading(false);
        setNotification({
          message:
            'No response received from MCP server. The request may have timed out.',
          type: NotificationType.Warning,
        });
      }, RESPONSE_TIMEOUT_MS);
    } catch (error: unknown) {
      pendingClientRequestIdRef.current = null;
      clearMcpResponseTimeout();
      setActiveNodeSockets((prev: string[]) =>
        prev.filter((item: string) => item !== id),
      );
      const normalizedError = ((): {
        statusCode?: number;
        message?: string;
      } => {
        if (
          error !== null &&
          typeof error === 'object' &&
          ('message' in error || 'statusCode' in error)
        ) {
          return error as { statusCode?: number; message?: string };
        }
        return {
          message: error instanceof Error ? error.message : String(error),
        };
      })();
      if (flowId) {
        executeErrorHandle(
          executeCounter,
          setExecuteCounter,
          setHasToExecute,
          data,
          edges,
          setNodeState,
          setSaving,
          id,
          flowId,
          setSchema,
          setNotification,
          normalizedError,
          userObjects,
        );
      }
      const fallbackMessage = normalizedError.message ?? 'Something went wrong';
      setFormData(prev => ({
        ...prev,
        text: fallbackMessage,
      }));
      onSave({ text: fallbackMessage }, 'execute');
      setNotification({
        message: fallbackMessage,
        type: NotificationType.Error,
      });
      setIsLoading(false);
    }
  }, [
    id,
    data,
    edges,
    flowId,
    formData,
    rawMcpServerUrl,
    schema,
    socket,
    organizationId,
    availableTools,
    setNotification,
    setActiveNodeSockets,
    pendingClientRequestIdRef,
    mcpResponseTimeoutRef,
    clearMcpResponseTimeout,
    executeCounter,
    setNodeState,
    setSaving,
    setSchema,
    setFormData,
    onSave,
    userObjects,
  ]);

  useEffect(() => {
    handleCallToolRef.current = handleCallTool;
  }, [handleCallTool]);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (
      readMcpFlowTriggerId(data) &&
      !data?.canceled &&
      !data?.paused
    ) {
      if (hasToExecute) {
        debounceTimeoutRef.current = setTimeout(() => {
          setHasToExecute(false);
          void handleCallToolRef.current();
        }, 1000);
      }
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [hasToExecute, formData, activeNodeSockets, data]);

  return {
    handleCallTool,
    isLoading,
    setIsLoading,
    hasToExecute,
    setHasToExecute,
  };
}
